import config from '../config';
import { authorizedFetch } from './request';

const API_V1 = `${config.apiUrl}/api/v1`;

/**
 * Handle non-OK responses by throwing a descriptive error.
 */
async function handleErrorResponse(response) {
  if (response.status === 401) {
    const error = new Error('Session expired. Please log in again.');
    error.code = 'UNAUTHORIZED';
    throw error;
  }
  if (response.status === 429) {
    // Backend sends a precise reason in `detail` (per-minute rate limit vs.
    // monthly quota/token limit). Surface it instead of a generic message.
    const detail = await response.json().then(b => b.detail).catch(() => null);
    const error = new Error(detail || 'Too many requests. Please slow down.');
    error.code = 'RATE_LIMITED';
    throw error;
  }
  if (!response.ok) {
    throw new Error(`Server error (${response.status})`);
  }
}

/**
 * Health check — GET /
 */
export async function healthCheck() {
  const response = await authorizedFetch(`${config.apiUrl}/`);
  await handleErrorResponse(response);
  return response.json();
}

/**
 * Change a rotation-expired password using the restricted Cognito session.
 */
export async function changeExpiredPassword({ currentPassword, newPassword, accessToken }) {
  const response = await authorizedFetch(`${API_V1}/auth/change-password`, {
    method: 'POST',
    headers: {
      'X-Cognito-Access-Token': accessToken,
    },
    body: JSON.stringify({
      current_password: currentPassword,
      new_password: newPassword,
    }),
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    const detail = body.detail;
    const message = typeof detail === 'string'
      ? detail
      : detail?.message || 'Password change failed';
    const error = new Error(message);
    error.code = detail?.code || 'PASSWORD_CHANGE_FAILED';
    throw error;
  }

  return response.json();
}

/**
 * Ask a question with SSE streaming — POST /ask/stream
 *
 * Callbacks:
 *   onStatus(status)       — "analyzing" | "searching" | "generating"
 *   onChunk(text)          — incremental text chunk
 *   onMetadata(metadata)   — { messageId, sources, confidence, analysis }
 *   onError(error)         — error object
 *   onDone()               — stream finished
 *
 * Returns an abort function to cancel the stream.
 */
export function askQuestionStream(question, sessionId, callbacks = {}) {
  const { onStatus, onChunk, onMetadata, onError, onDone } = callbacks;
  const abortController = new AbortController();

  (async () => {
    try {
      const body = { question };
      if (sessionId) body.session_id = sessionId;

      const response = await authorizedFetch(`${API_V1}/ask/stream`, {
        method: 'POST',
        body: JSON.stringify(body),
        signal: abortController.signal,
      });

      await handleErrorResponse(response);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line in buffer

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          // Handle SSE data: prefix
          const data = trimmed.startsWith('data: ')
            ? trimmed.slice(6)
            : trimmed;

          if (data === '[DONE]') {
            onDone?.();
            return;
          }

          try {
            const event = JSON.parse(data);
            switch (event.type) {
              case 'status':
                onStatus?.(event.status);
                break;
              case 'chunk':
                onChunk?.(event.content);
                break;
              case 'metadata':
                onMetadata?.({
                  messageId: event.message_id,
                  sources: event.sources || [],
                  confidence: event.confidence,
                  analysis: event.analysis,
                });
                break;
              case 'error': {
                const message = typeof event.error === 'string'
                  ? event.error
                  : 'Stream failed';
                const error = new Error(message);
                error.code = event.code === 'service_unavailable'
                  ? 'SERVICE_UNAVAILABLE'
                  : message.toLowerCase().includes('limit')
                  ? 'RATE_LIMITED'
                  : 'STREAM_ERROR';
                onError?.(error);
                await reader.cancel().catch(() => {});
                abortController.abort();
                return;
              }
              default:
                break;
            }
          } catch {
            // not JSON, might be a raw text line — treat as chunk
            if (data.length > 0) onChunk?.(data);
          }
        }
      }

      onDone?.();
    } catch (err) {
      if (err.name === 'AbortError') return;
      onError?.(err);
    }
  })();

  return () => abortController.abort();
}

/**
 * Search documents — POST /search
 * Returns array of { content, source }
 */
export async function searchDocuments(question, k = 3) {
  const response = await authorizedFetch(`${API_V1}/search?k=${k}`, {
    method: 'POST',
    body: JSON.stringify({ question }),
  });
  await handleErrorResponse(response);
  return response.json();
}

/**
 * 認証ユーザーのチャットルーム一覧を取得する。
 */
export async function listChatRooms() {
  const response = await authorizedFetch(`${API_V1}/chatrooms`);
  await handleErrorResponse(response);
  return response.json();
}

/**
 * チャットルームの履歴を取得する。一覧には履歴が含まれないため、ルームを開いた時に呼ぶ。
 */
export async function getChatRoomHistory(sessionId) {
  const response = await authorizedFetch(`${API_V1}/chatrooms/${sessionId}/history`);
  await handleErrorResponse(response);
  return response.json();
}

/**
 * チャットルームが存在しない場合は作成する。
 */
export async function createChatRoom(sessionId, title) {
  const body = {};
  if (sessionId) body.session_id = sessionId;
  if (title) body.title = title;

  const response = await authorizedFetch(`${API_V1}/chatrooms`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  await handleErrorResponse(response);
  return response.json();
}

/**
 * チャットルームのメタデータを更新する。
 */
export async function updateChatRoom(sessionId, { title } = {}) {
  const response = await authorizedFetch(`${API_V1}/chatrooms/${sessionId}`, {
    method: 'PATCH',
    body: JSON.stringify({ title }),
  });
  await handleErrorResponse(response);
  return response.json();
}

/**
 * チャットルームと履歴を削除する。
 */
export async function deleteChatRoom(sessionId) {
  const response = await authorizedFetch(`${API_V1}/chatrooms/${sessionId}`, {
    method: 'DELETE',
  });
  await handleErrorResponse(response);
  return response.json();
}

/**
 * Submit feedback — POST /feedback
 * Rating: 'thumbs_up' or 'thumbs_down'
 */
export async function submitFeedback(sessionId, messageId, rating) {
  const response = await authorizedFetch(`${API_V1}/feedback`, {
    method: 'POST',
    body: JSON.stringify({
      session_id: sessionId,
      message_id: messageId,
      rating,
    }),
  });
  await handleErrorResponse(response);
  return response.json();
}

/**
 * Delete a session — DELETE /session/{sessionId}
 */
export async function deleteSession(sessionId) {
  const response = await authorizedFetch(`${API_V1}/session/${sessionId}`, {
    method: 'DELETE',
  });
  await handleErrorResponse(response);
  return response.json();
}

/**
 * Get quota usage — GET /quota/usage
 * Returns { used, remaining, limit, reset_date, current_month }
 */
export async function getQuotaUsage() {
  const response = await authorizedFetch(`${API_V1}/quota/usage`);
  await handleErrorResponse(response);
  return response.json();
}

/**
 * Get token usage — GET /token/usage
 * Returns { input_tokens, output_tokens, total_used, remaining, limit, reset_date, current_month }
 */
export async function getTokenUsage() {
  const response = await authorizedFetch(`${API_V1}/token/usage`);
  await handleErrorResponse(response);
  return response.json();
}

/**
 * Get the caller's live role and permissions — GET /me
 * Returns { username, role, tabs, manageable_roles }
 */
export async function getMe() {
  const response = await authorizedFetch(`${API_V1}/me`);
  await handleErrorResponse(response);
  return response.json();
}

/**
 * Get monthly usage in one request — GET /usage
 * Returns { quota, tokens }
 */
export async function getUsage() {
  const response = await authorizedFetch(`${API_V1}/usage`);
  await handleErrorResponse(response);
  return response.json();
}
/**
 * FAQソースをPDFとして取得し、プレビュー用のblob URLを返す。
 * `path` は回答のソースに入っている `/api/v1/sources/pdf?key=...`。
 */
export async function fetchSourcePdf(path) {
  const response = await authorizedFetch(`${config.apiUrl}${path}`);
  await handleErrorResponse(response);
  return URL.createObjectURL(await response.blob());
}

// ソースファイルを選択したらドキュメントをプレビューするためのAPI呼び出し。
export async function getDocumentContent(fileName, displayName = "") {
  const response = await authorizedFetch(
    `${API_V1}/sources/preview?file_name=${encodeURIComponent(fileName)}&name=${encodeURIComponent(displayName)}`
  );

  await handleErrorResponse(response);

  const contentType = response.headers.get("content-type");

  if (contentType?.includes("application/pdf")) {
    const blob = await response.blob();

    return {
      type: "pdf",
      url: URL.createObjectURL(blob),
      name: displayName || fileName,
    };
  }

  // DOCX/text response (JSON)
  return {
    type: "text",
    ...(await response.json()),
  };
}
export async function downloadDocument(fileName) {
  const response = await authorizedFetch(
    `${API_V1}/sources/download?file_name=${encodeURIComponent(fileName)}`
  );

  if (!response.ok) {
    throw new Error(`Failed to download document (${response.status})`);
  }

  return response.blob();
}
